import { computed } from "vue";
import { useDashboardTab } from "@/composables/admin/dashboard/useDashboardTab";
import { getSales } from "@/services/dashboard/dashboard";
import { getSeasonName } from "@/composables/admin/dashboard/seasonUtils";

/**
 * Composable del panel de ventas del dashboard.
 * Muestra donuts online/taquilla y anticipadas, y línea de evolución digital.
 *
 * @param {import('vue').Ref<number[]>} seasonIdsRef
 * @param {import('vue').Ref<Array>} seasonsRef
 * @returns {{
 *     data: import('vue').Ref<object|null>,
 *     loading: import('vue').Ref<boolean>,
 *     error: import('vue').Ref<string|null>,
 *     isMultiSeason: import('vue').ComputedRef<boolean>,
 *     onlineDonutData: import('vue').ComputedRef<Array>,
 *     digitalLineData: import('vue').ComputedRef<Array>,
 *     anticipadasDonutData: import('vue').ComputedRef<Array>,
 *     seasonBreakdown: import('vue').ComputedRef<Array|null>,
 *     refresh: () => Promise<void>,
 * }}
 */
export function useSales(seasonIdsRef, seasonsRef) {
    const base = useDashboardTab(seasonIdsRef, seasonsRef, getSales);

    /**
     * Datos para donut online vs taquilla.
     * Single: [['Canal', 'Total'], ['Online', val], ['Taquilla', val]]
     * Multi:  [['Temporada', 'Online', 'Taquilla'], [name, v1, v2], ...]
     */
    const onlineDonutData = computed(() => {
        const perSeason = base.data.value?.data?.per_season || {};
        const entries = Object.entries(perSeason).filter(
            ([, entry]) => entry?.online_vs_taquilla,
        );
        if (entries.length === 0) return [];

        if (base.isMultiSeason.value) {
            const header = ["Temporada", "Online", "Taquilla"];
            const rows = entries.map(([seasonId, entry]) => {
                const ovt = entry.online_vs_taquilla;
                return [
                    getSeasonName(seasonsRef.value, seasonId),
                    ovt?.online?.total || 0,
                    ovt?.taquilla?.total || 0,
                ];
            });
            return [header, ...rows];
        }

        const ovt = entries[0][1].online_vs_taquilla;
        const fmt = v => ({ v, f: `${v.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}€` });
        return [
            ["Canal", "Total"],
            ["Online", fmt(ovt?.online?.total || 0)],
            ["Taquilla", fmt(ovt?.taquilla?.total || 0)],
        ];
    });

    /**
     * Datos para línea de evolución digital (% online por jornada).
     * Single: [['Jornada', '% Online'], [j1, val], ...]
     * Multi:  [['Jornada', 'S1', 'S2', ...], [j1, v1, v2, ...], ...]
     */
    const digitalLineData = computed(() => {
        const perSeason = base.data.value?.data?.per_season || {};
        const entries = Object.entries(perSeason).filter(
            ([, entry]) => entry?.digital_evolution?.length,
        );
        if (entries.length === 0) return [];

        if (base.isMultiSeason.value) {
            const allLabels = new Set();
            const seasonData = {};
            for (const [seasonId, entry] of entries) {
                const name = getSeasonName(seasonsRef.value, seasonId);
                seasonData[name] = entry.digital_evolution || [];
                for (const d of entry.digital_evolution) {
                    allLabels.add(d.jornada);
                }
            }
            // Orden numérico para que las jornadas se muestren correctamente
            const sortedLabels = Array.from(allLabels).sort(
                (a, b) => Number(a) - Number(b),
            );
            const names = Object.keys(seasonData);
            const header = ["Jornada", ...names];
            const rows = sortedLabels.map(label => {
                const row = [{v: Number(label), f: `Jornada ${label}`}];
                for (const name of names) {
                    const d = seasonData[name].find(d => d.jornada === label);
                    row.push(d ? Number(d.online_pct || 0) : null);
                }
                return row;
            });
            return [header, ...rows];
        }

        const evolution = entries[0][1].digital_evolution || [];
        const header = ["Jornada", "% Online"];
        const rows = evolution.map(d => [{v: Number(d.jornada), f: `Jornada ${d.jornada}`}, Number(d.online_pct || 0)]);
        return [header, ...rows];
    });

    /**
     * Datos para donut anticipadas vs último momento.
     * Single: [['Tipo', 'Total'], ['Anticipadas', val], ['Último momento', val]]
     * Multi:  [['Temporada', 'Anticipadas', 'Último momento'], ...]
     */
    const anticipadasDonutData = computed(() => {
        const perSeason = base.data.value?.data?.per_season || {};
        const entries = Object.entries(perSeason).filter(
            ([, entry]) => entry?.anticipadas_vs_ultimo_momento,
        );
        if (entries.length === 0) return [];

        if (base.isMultiSeason.value) {
            const header = ["Temporada", "Anticipadas", "Último momento"];
            const rows = entries.map(([seasonId, entry]) => {
                const avu = entry.anticipadas_vs_ultimo_momento;
                return [
                    getSeasonName(seasonsRef.value, seasonId),
                    avu?.anticipadas?.total || 0,
                    avu?.ultimo_momento?.total || 0,
                ];
            });
            return [header, ...rows];
        }

        const avu = entries[0][1].anticipadas_vs_ultimo_momento;
        const aTotal = avu?.anticipadas?.total || 0;
        const uTotal = avu?.ultimo_momento?.total || 0;
        const fmtCurrency = v => v.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        return [
            ["Tipo", "Total"],
            ["Anticipadas", {v: aTotal, f: `${fmtCurrency(aTotal)}€`}],
            ["Último momento", {v: uTotal, f: `${fmtCurrency(uTotal)}€`}],
        ];
    });

    /**
     * Desglose por temporada para tabla comparativa.
     */
    const seasonBreakdown = computed(() => {
        const perSeason = base.data.value?.data?.per_season || {};
        const entries = Object.entries(perSeason).filter(
            ([, entry]) => entry?.online_vs_taquilla,
        );
        if (entries.length === 0) return null;

        return entries.map(([seasonId, entry]) => {
            const ovt = entry.online_vs_taquilla || {};
            const avu = entry.anticipadas_vs_ultimo_momento || {};
            return {
                season: getSeasonName(seasonsRef.value, seasonId),
                onlineTotal: ovt?.online?.total || 0,
                taquillaTotal: ovt?.taquilla?.total || 0,
                anticipadasTotal: avu?.anticipadas?.total || 0,
                ultimoMomentoTotal: avu?.ultimo_momento?.total || 0,
            };
        });
    });

    /** Opciones para donut de ventas online vs taquilla */
    const donutChartOptions = {
        pieHole: 0.5,
        pieSliceText: 'percentage',
        pieSliceTextStyle: { fontSize: 13, bold: true },
        legend: { position: "bottom" },
        chartArea: { width: "80%", height: "75%" },
        colors: ["#e30613", "#6b7280", "#2563eb", "#059669", "#d97706"],
    };

    /** Opciones para línea de evolución digital */
    const lineChartOptions = {
        legend: { position: "bottom" },
        chartArea: { width: "85%", height: "70%" },
        colors: ["#2563eb"],
        vAxis: { minValue: 0, maxValue: 100, title: "%" },
        curveType: "function",
    };

    /** Horas del umbral "último momento" desde la API */
    const thresholdHours = computed(() => {
        const perSeason = base.data.value?.data?.per_season || {};
        const entry = Object.values(perSeason)[0];
        return entry?.anticipadas_vs_ultimo_momento?.threshold_hours || 24;
    });

    /** Tipo de chart: ColumnChart para multi-season, PieChart para single */
    const onlineChartType = computed(() =>
        base.isMultiSeason.value ? "ColumnChart" : "PieChart",
    );
    const anticipadasChartType = computed(() =>
        base.isMultiSeason.value ? "ColumnChart" : "PieChart",
    );

    return {
        data: base.data,
        loading: base.loading,
        error: base.error,
        isMultiSeason: base.isMultiSeason,
        onlineDonutData,
        digitalLineData,
        anticipadasDonutData,
        seasonBreakdown,
        donutChartOptions,
        lineChartOptions,
        onlineChartType,
        anticipadasChartType,
        thresholdHours,
        refresh: base.refresh,
    };
}
